import { useRef, useLayoutEffect, useState } from 'react';
import {
  getCalendarDays,
  formatDate,
  isSameDay,
  getLunarInfo,
  getHoliday,
} from '@daynote/utils';
import type { DayNote } from '@daynote/shared-types';

interface Props {
  month: string;
  dayNotesByDate: Record<string, DayNote>;
  showLunar?: boolean;
  showHoliday?: boolean;
  weekStart?: 0 | 1;
  /** 屏幕内完整显示的周行数，超出时滚动 */
  maxRows?: number;
  onDayClick: (date: string) => void;
}

export function MonthGridMobile({
  month,
  dayNotesByDate,
  showLunar = true,
  showHoliday = true,
  weekStart = 1,
  maxRows = 5,
  onDayClick,
}: Props) {
  const today = new Date();
  const days = getCalendarDays(month, weekStart);
  const [, m] = month.split('-').map(Number);

  const weeks: Date[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  const containerRef = useRef<HTMLDivElement>(null);
  const [rowHeight, setRowHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const h = el.getBoundingClientRect().height;
      if (h > 0) setRowHeight(h / maxRows);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [maxRows]);

  const rowCount = weeks.length;
  const gridAutoRows = rowCount <= maxRows
    ? '1fr'
    : rowHeight ? `${rowHeight}px` : '1fr';
  const gridHeight = rowCount <= maxRows ? '100%' : 'auto';

  return (
    <div ref={containerRef} className="flex-1 overflow-auto min-h-0">
      <div
        className="grid"
        style={{
          gridTemplateColumns: 'repeat(7, 1fr)',
          gridAutoRows,
          height: gridHeight,
        }}
      >
        {days.map((day) => {
          const dateStr = formatDate(day);
          const isCurrentMonth = day.getMonth() + 1 === m;
          const isToday = isSameDay(day, today);
          const lunar = getLunarInfo(dateStr);
          const holiday = showHoliday ? getHoliday(dateStr) : null;
          const dn = dayNotesByDate[dateStr];
          const coloredItems =
            dn?.items.filter((it) => it.color ?? it.group?.color).slice(0, 4) ?? [];

          return (
            <button
              key={dateStr}
              type="button"
              onClick={() => onDayClick(dateStr)}
              className={`relative border-r border-b border-[var(--grid-line)] p-1.5 flex flex-col items-center justify-between overflow-hidden ${
                !isCurrentMonth ? 'opacity-40' : ''
              } ${isToday ? 'bg-[var(--panel-today)]' : ''}`}
            >
              {isToday && (
                <span className="absolute inset-0.5 border-2 border-daynote-accent rounded pointer-events-none" />
              )}
              {holiday && (
                <span
                  className={`absolute top-1 right-1 w-3 h-3 flex items-center justify-center text-[8px] font-bold text-white rounded-sm ${
                    holiday.type === 'rest' ? 'bg-daynote-rest' : 'bg-daynote-work'
                  }`}
                >
                  {holiday.type === 'rest' ? '休' : '班'}
                </span>
              )}

              <div className="flex flex-col items-center gap-0.5 z-10">
                <span
                  className={`font-display text-[17px] font-medium leading-none ${isToday ? 'text-daynote-accent font-semibold' : ''}`}
                >
                  {day.getDate()}
                </span>
                {showLunar && (
                  <span className="text-[9px] text-daynote-text/50 tracking-wide">{lunar.lunarDay}</span>
                )}
                {holiday && (
                  <span className="text-[9px] text-daynote-warm font-medium">{holiday.name}</span>
                )}
                {showLunar && lunar.solarTerm && !holiday && (
                  <span className="text-[9px] text-daynote-warm font-medium">{lunar.solarTerm}</span>
                )}
              </div>

              <div className="flex items-center gap-0.5 min-h-2.5 z-10">
                {coloredItems.length > 0 && (
                  <div className="flex gap-0.5 flex-wrap justify-center max-w-8">
                    {coloredItems.map((it) => (
                      <span
                        key={it.id}
                        className="w-[5px] h-[5px] rounded-full inline-block"
                        style={{ background: (it.color ?? it.group?.color)! }}
                      />
                    ))}
                  </div>
                )}
                {dn && dn.items.length > 0 && (
                  <span className="text-[9px] text-daynote-text/50 font-display">{dn.items.length}</span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
